import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/Button";
import { useModal } from "../../../../../hooks/useModal";
import { CancelButton } from "../../../../PatientPage/components/PatientAlertsTable/components/ManageTriggersModal/ManageTriggersModal.styles";
import {
  Container,
  Subtitle,
  SubmitContainer,
  Title,
} from "../../../../PatientPage/components/PatientLateralMenu/components/ConfirmChangesModal.styles";

interface IConfirmActionModalProps {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm(): Promise<unknown> | void;
  onClose(): void;
}

/** EN-5261: the portal's confirmation modal for a builder action (delete, discard, make live). */
export function ConfirmActionModal({
  title,
  body,
  confirmLabel,
  onConfirm,
  onClose,
}: IConfirmActionModalProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      onClose();
    }
  };

  return (
    <Container>
      <Title>{title}</Title>
      <Subtitle larger>{body}</Subtitle>
      <SubmitContainer>
        <Button
          type="button"
          label={confirmLabel}
          isLoading={isLoading}
          disabled={isLoading}
          onClick={handleConfirm}
        />
        <CancelButton onClick={onClose} style={{ marginLeft: "10px" }}>
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </Container>
  );
}

/** Opens ConfirmActionModal; the action runs on confirm, then the modal closes. */
export function useConfirmAction() {
  const { openModal, closeModal } = useModal();

  return (props: Omit<IConfirmActionModalProps, "onClose">) =>
    openModal(<ConfirmActionModal {...props} onClose={closeModal} />, {
      width: "560px",
      height: "auto",
      showCloseButton: true,
    });
}
